// @flow
import uniq from 'lodash/fp/uniq';
import last from 'lodash/fp/last';
import flatten from 'lodash/fp/flatten';

import { locationOption } from './location';
import { getField } from './model';
import { conditionMatcherFor } from './object-query-matcher';
// types
import type { DropShipValue } from './drop-ship';
import type { Location, LocationOption } from './location';
import type { ProductNote } from './product-note';
import type { User } from './user';
import type { ObjectQueryMatcher } from './object-query-matcher';
import type { AbstractCatalog } from './abstract-catalog';
import type { CatalogPermissionHash } from './catalog';
import type { ROA, ROE } from './type';

import type { FlashType } from 'ui/private/flash-message';
import { type TagTree, topLevelPathsCollapsed } from 'modules/tag-tree';
import { type Facet } from 'modules/facet';
import { dateString, today } from 'modules/date';

/**
 * @module modules/customer
 */

export type Warehouse = {|
  _id?: string,
  key: string,
  name: string,
|};

/**
 * @typedef Customer
 */

// "let's assume the same keys as your current nls keys"
type AccountNotice =
  | 'customer_error_block'
  | 'customer_error_deleted'
  | 'customer_error_prospect'
  | 'customer_error_risky'
  | 'customer_error_invalid';

type Address = ROE<{
  _id: string,
  city: string | null,
  country: string | null,
  state: string | null,
  street1: string | null,
  street2: string | null,
  street3: string | null,
  zip: string | null,
}>;

export type Customer = {
  _id: string,
  currency_code: string,
  wholesale_price_group: string,
  retail_price_group: string,
  // key matches QuantityRecord.source, name is the name to be displayed
  warehouses?: Array<Warehouse>,
  locations?: Array<Location>,
  client_fields?: {
    drop_ship?: DropShipValue,
    artifi_client_code?: string,
    ...
  },
  number: string,
  name: string,
  address_lines: ROA<string>,
  catalog_permissions?: CatalogPermissionHash,
  tags?: {
    credit_status?: string[],
    treat_wholesale_as_discounted?: boolean,
  },
  document_create_disabled?: boolean,
  document_edit_disabled?: boolean,
  document_submit_disabled?: boolean,
  account_notice?: AccountNotice,
  region?: string,
  show_order_type?: boolean,
  b2c?: boolean,
  document_duplicated_for?: boolean,
  custom_catalog_shared_to?: boolean,
  // this field is meant to allow access to the catalogs
  // with the classification type prebook
  // this setting in the admin user area (CSP)
  // can be used to overrule the setting visible_to
  // on the catalog when that is limited to only reps
  prebook_access?: boolean | null,
  phone: string,
  address?: Address,
};

export type OrderTypeOption = {
  label: string,
  value: string,
};

export type OrderTypes = {
  ...ObjectQueryMatcher,
  options: OrderTypeOption[],
  customerPermissions?: boolean,
  defaultValue?: string,
};

export type SerializedCustomersQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<Customer>,
  range: string,
  facets: $ReadOnlyArray<Facet>,
|}>;

export type CustomersQueryResponse = $ReadOnly<{|
  results: $ReadOnlyArray<Customer>,
  total: number,
  facets: $ReadOnlyArray<Facet>,
|}>;

export type CustomersQuery = $ReadOnly<{|
  keyword: string | null,
  tagPaths: $ReadOnlyArray<$ReadOnlyArray<string>>,
  sortBy?: 'number' | 'name' | 'region',
  sortDirection?: 'ASC' | 'DESC',
  Range?: string,
  rep2repsharing?: boolean,
  catalog?: AbstractCatalog,
  orderId?: string,
  customCatalogId?: string,
|}>;

export type OrderTypesSpecifications = OrderTypes[];

export type CustomerTagTreeResponse = TagTree;

type StringSearcher<T> = (search: string, list: ROA<T>) => ROA<T>;

/**
 * Get the `currencyCode` from the Customer.
 *
 * @memberof modules/customer
 */
export const currencyCode = (customer: Customer): string =>
  (getField(customer, 'currencyCode'): string);

/**
 * Get the `retailPriceGroup` from the Customer.
 *
 * @memberof modules/customer
 */
export const retailPriceGroup = (customer: Customer): string =>
  (getField(customer, 'retailPriceGroup'): string);

/**
 * Get the `wholesalePriceGroup` from the Customer.
 *
 * @memberof modules/customer
 */
export const wholesalePriceGroup = (customer: Customer): string =>
  (getField(customer, 'wholesalePriceGroup'): string);

// $FlowFixMe[prop-missing]
export const makeCustomer = (): Customer => ({
  address_lines: [],
  currency_code: 'USD',
  name: '',
  number: '',
  retail_price_group: 'retail',
  wholesale_price_group: 'wholesale',
});

export const clientFieldsForCustomer = (
  customer: Customer,
): { [key: string]: any } => getField(customer, 'clientFields') || { ...null };

const orderTypeConfig = (
  orderTypesSpecifications: OrderTypesSpecifications,
  customer: Customer,
  catalog: AbstractCatalog,
  user: User,
): OrderTypes | null =>
  orderTypesSpecifications
    .filter(conditionMatcherFor(user, 'user'))
    .filter(conditionMatcherFor(customer, 'customer'))
    .filter(conditionMatcherFor(catalog, 'catalog'))[0] || null;

export const quiksilverNonContractDefaultOrderType = (
  customer: Customer,
  catalog: AbstractCatalog,
): string | null => {
  if (customer.show_order_type === false) {
    const firstShip = catalog.dates?.first_ship || dateString(today());
    if (
      catalog.classification === 'Prebook' &&
      dateString(today()) < firstShip
    ) {
      return 'ZSO-002';
    }
    return 'ZSO-004';
  }
  return null;
};

export const defaultOrderType = (
  orderTypesSpecifications: OrderTypesSpecifications,
  customer: Customer,
  catalog: AbstractCatalog,
  user: User,
  clientName?: string,
): string | void => {
  if (clientName === 'Boardriders') {
    const type = quiksilverNonContractDefaultOrderType(customer, catalog);
    if (type !== null) return type;
  }
  return orderTypeConfig(orderTypesSpecifications, customer, catalog, user)
    ?.defaultValue;
};

export const orderTypes = (
  orderTypesSpecifications: OrderTypesSpecifications,
  customer: Customer,
  catalog: AbstractCatalog,
  user: User,
): OrderTypeOption[] => {
  const config = orderTypeConfig(
    orderTypesSpecifications,
    customer,
    catalog,
    user,
  );

  if (!config) return [];

  const { customerPermissions, options = [] } = config;

  const { show_order_type: showOrderType } = customer;
  if (user.type !== 'rep' && customerPermissions && !showOrderType) {
    return [];
  }
  return Array.isArray(options) ? options : [];
};

const stringsMatchSearch = (search: string, strings: ROA<string>): boolean =>
  strings.some((s) => (s || '').toLowerCase().includes(search.toLowerCase()));

const stringSearcher =
  <T>(stringsFinder: (T) => ROA<string>): StringSearcher<T> =>
  (search: string, list: ROA<T>): ROA<T> =>
    search.trim()
      ? list.filter((item) => stringsMatchSearch(search, stringsFinder(item)))
      : ([]: ROA<T>);

const customersByName = stringSearcher((c) => [c.name]);
const customersByNumber = stringSearcher((c) => [c.number]);
const customersByLocation = stringSearcher((c) => [...c.address_lines]);
const customerByShipToName = stringSearcher((c) =>
  c.locations ? c.locations.map((l) => l.name) : [],
);
const customersByShipToNumber = stringSearcher((c) =>
  c.locations ? c.locations.map((l) => l.number) : [],
);
const customersByShipToLocation = stringSearcher((c) =>
  c.locations ? flatten(c.locations.map((l) => l.address_lines)) : [],
);

export const customersByQuery = (
  searchCustomerName: string,
  searchCustomerNumber: string,
  searchCustomerLocation: string,
  searchShipToName: string,
  searchShipToNumber: string,
  searchShipToLocation: string,
  customers: ROA<Customer>,
): ROA<Customer> => {
  const resultsByCustomerName = searchCustomerName
    ? customersByName(searchCustomerName, customers)
    : [];

  const resultsByCustomerNumber = searchCustomerNumber
    ? customersByNumber(searchCustomerNumber, customers)
    : [];

  const resultsByCustomerLocation = searchCustomerLocation
    ? customersByLocation(searchCustomerLocation, customers)
    : [];

  const resultsByShipToName = searchShipToName
    ? customerByShipToName(searchShipToName, customers)
    : [];

  const resultsByShipToNumber = searchShipToNumber
    ? customersByShipToNumber(searchShipToNumber, customers)
    : [];

  const resultsByShipToLocation = searchShipToLocation
    ? customersByShipToLocation(searchShipToLocation, customers)
    : [];

  return uniq([
    ...resultsByCustomerName,
    ...resultsByCustomerNumber,
    ...resultsByCustomerLocation,
    ...resultsByShipToName,
    ...resultsByShipToNumber,
    ...resultsByShipToLocation,
  ]);
};

export const notes = (
  customerNumber: string,
  productNotes: ProductNote[],
): ProductNote[] =>
  productNotes.filter((n) => n.customerNumber === customerNumber);

export const locations = (customer: Customer): Location[] =>
  customer.locations || [];

export const locationOptions = (
  customer: Customer,
  includeLocationNumberInSelect?: boolean,
): LocationOption[] =>
  locations(customer).map((location) =>
    locationOption(location, includeLocationNumberInSelect),
  );

export const byNumber = (
  haystack: ROA<Customer>,
  needle: string,
): Customer | null => haystack.find(({ number }) => number === needle) || null;

export const canCreateOrders = (subject: Customer): boolean =>
  !subject.document_create_disabled;

export const canOpenOrders = (subject: Customer): boolean =>
  !subject.document_edit_disabled;

export const canSubmitOrders = (subject: Customer): boolean =>
  !subject.document_submit_disabled;

export const accountNotice = (subject: Customer): ?string =>
  subject.account_notice;

export const treatWholesaleAsDiscounted = (customer: Customer): boolean =>
  Boolean(
    (getField(customer, 'tags') || { ...null }).treat_wholesale_as_discounted,
  );

export const customersQueryAsQueryString = ({
  keyword,
  tagPaths,
  sortBy,
  sortDirection,
  rep2repsharing,
  catalog,
  Range,
  orderId,
  customCatalogId,
}: CustomersQuery): string => {
  const params = new URLSearchParams();

  if (keyword) params.append('keyword', keyword);

  const userTypes = ['rep', 'dealer', 'internal'];

  const queryType = userTypes.find((type) =>
    tagPaths.map((path) => path[0]).includes(type),
  );

  if (queryType) params.append('type', queryType);

  const adjustedTagPaths = topLevelPathsCollapsed(
    tagPaths.map((path) =>
      path.filter((part, idx) => idx > 0 || !userTypes.includes(part)),
    ),
  );

  adjustedTagPaths.forEach((path) => {
    const value = last(path);
    const body = path.slice(0, -1);

    if (body.length === 0) return;

    params.append(`tags[${body.join('][')}][]`, value);
  });

  if (catalog) params.append('catalog_key', catalog.key);

  if (Range) params.append('Range', Range);

  if (orderId) params.append('document_id', orderId);

  if (customCatalogId) params.append('custom_catalog_id', customCatalogId);

  params.append('rep2repsharing', rep2repsharing ? 'true' : 'false');

  const sortDirectionSymbol = (sortDirection || 'DESC') === 'DESC' ? '-' : '+';

  const sortParam = encodeURIComponent(
    sortBy ? `sort(${sortDirectionSymbol}${sortBy})` : '',
  );

  return `${params.toString()}&${sortParam}`;
};

export const deserializedCustomersQueryResponse = ({
  results,
  facets,
  range,
}: SerializedCustomersQueryResponse): CustomersQueryResponse => ({
  results,
  facets,
  total: Number(last(range.split('/'))),
});

export const isShared = (customer: Customer): boolean =>
  Boolean(
    customer.document_duplicated_for || customer.custom_catalog_shared_to,
  );

export type CustomerStatus = {
  message: string,
  level: FlashType | 'none',
};

export const dealerAsCustomerFlash = (subject?: ?Customer): CustomerStatus => {
  if (!subject) {
    return {
      level: 'none',
      message: 'customer_error_block',
    };
  }
  if (!canCreateOrders(subject)) {
    return {
      level: 'error',
      message: accountNotice(subject) || 'customer_error_block',
    };
  }
  if (!canOpenOrders(subject)) {
    return {
      level: 'warning',
      message: accountNotice(subject) || 'customer_error_block',
    };
  }
  return {
    level: 'none',
    message: accountNotice(subject) || 'customer_error_block',
  };
};

export const customerFlash = (subject?: ?Customer): CustomerStatus => {
  if (!subject) {
    return {
      level: 'none',
      message: 'customer_error_block',
    };
  }
  if (!canCreateOrders(subject)) {
    return {
      level: 'error',
      message: accountNotice(subject) || 'customer_error_block',
    };
  }
  if (!canOpenOrders(subject) || !canSubmitOrders(subject)) {
    return {
      level: 'warning',
      message: accountNotice(subject) || 'customer_error_block',
    };
  }
  return {
    level: 'none',
    message: accountNotice(subject) || 'customer_error_block',
  };
};

export const customerCanOpenBuilder = (customer?: Customer): boolean =>
  !!customer && canCreateOrders(customer) && canOpenOrders(customer);
